import {
    UrlParameter,
    WSW_DOMAIN_SUFFIX,
    WSW_FIPS_DOMAIN_SUFFIX,
} from "@constants/domain";
import { CustomCookieIdentifier } from "@constants/shortbreadConfig";
import { validateDeepLink } from "./deepLinkUtils";
import { getLogger } from "./ermineLoggerUtils";
import { ExperimentalFlags } from "@customTypes/features";
import { getCookieManager } from "./cookieManagerUtils";
import { MetricName } from "@constants/logging";

/**
 * Evaluates if the current domain is custom domain or follows our default domain
 * naming convention: `<uuid>.workspaces-web(-fips).com`
 * @returns true if custom domain, false otherwise, throws exception if hostname
 * is empty
 */
export function isCustomDomain(): boolean {
    const hostname: string = window.location.hostname;
    if (
        hostname.endsWith(WSW_DOMAIN_SUFFIX) ||
        hostname.endsWith(WSW_FIPS_DOMAIN_SUFFIX)
    ) {
        return false;
    } else {
        return true;
    }
}

/**
 * Checks the current web app domain to evaluate the portal parent domain. This
 * domain allows users to create cookies which can be accessed by different
 * portals.
 *
 * This functions by making use of WSW's `<uuid>.workspaces-web(-fips).com`
 * domain convention.
 * @returns domain for cross portal cookie or hostname if custom Domain
 */
export function getCrossPortalParentCookieDomain(): string {
    const hostname: string = window.location.hostname;
    if (hostname.endsWith(WSW_DOMAIN_SUFFIX)) {
        return WSW_DOMAIN_SUFFIX;
    } else if (hostname.endsWith(WSW_FIPS_DOMAIN_SUFFIX)) {
        return WSW_FIPS_DOMAIN_SUFFIX;
    } else {
        return hostname;
    }
}

export const getAuthErrorQueryParam = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    return {
        errorCode: urlSearchParams.get("error"),
        errorDescription: urlSearchParams.get("error_description"),
    };
};

/**
 * If the experimentalToolbar URL parameter is empty, we will use the previous setting.
 * Flag value will be converted into boolean type and the value will be `true` if it is
 * case-insensitively equal to "true"
 */
export const setExperimentalFlagsFromUrlParams = (
    urlParams: URLSearchParams
) => {
    const cookieManagerInstance = getCookieManager();
    const flags = [
        UrlParameter.LEGACY,
        UrlParameter.APPS,
        UrlParameter.MOBILE_DEVICE,
    ].map((param) => ({ flagName: param, flagValue: urlParams.get(param) }));

    let experimentalFlags: ExperimentalFlags;
    try {
        experimentalFlags = JSON.parse(
            cookieManagerInstance.getSessionStorage(
                CustomCookieIdentifier.EXPERIMENTAL.name
            ) ?? "{}"
        ) as ExperimentalFlags;
    } catch {
        experimentalFlags = {};
    }

    const updateExperimentalFlags = flags
        .filter(({ flagValue }) => flagValue != null)
        .reduce(
            (result, { flagName, flagValue }) => ({
                ...result,
                [flagName]: /true/i.test(flagValue),
            }),
            experimentalFlags
        );

    Object.entries(updateExperimentalFlags)
        .filter(([, flagValue]) => flagValue)
        .forEach(([flagName]) => {
            getLogger().publishMetricWithDimensions(
                MetricName.EXPERIMENTAL_FLAG,
                { Flag: flagName }
            );
        });

    cookieManagerInstance.setSessionStorage(
        CustomCookieIdentifier.EXPERIMENTAL.name,
        JSON.stringify(updateExperimentalFlags)
    );
};

export const setDeepLinkCookieFromUrlParams = (urlParams: URLSearchParams) => {
    const deepLinkInput = urlParams.get(UrlParameter.DEEPLINK);

    if (deepLinkInput == null) {
        return;
    }

    let decodedDeepLinkInput = decodeURIComponent(deepLinkInput);
    if (!validateDeepLink(decodedDeepLinkInput)) {
        decodedDeepLinkInput = null;
        void getLogger().error("Invalid DeepLink input");
    }
    getCookieManager().setSessionStorage(
        CustomCookieIdentifier.DEEPLINK.name,
        decodedDeepLinkInput
    );
};
